/**
 * @fileoverview AdminAiUsageTable — platform-wide AI spend by org
 *
 * Lives on the admin overview page. Super-admin only — fetched via
 * `getPlatformUsageBreakdown` which itself enforces the role.
 *
 * Each row: org name, this-month spend, status pill, editable monthly
 * cap, in/out token totals. Sorted by spend descending so the heaviest
 * spenders are at the top. Owners can override a firm's cap inline
 * (admin.organisations.setAiUsageCap) to lift or lower its limit; a
 * capped firm unblocks the moment its cap is raised.
 */

import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  Sparkles,
  AlertTriangle,
  OctagonAlert,
  CheckCircle2,
  Loader2,
  Pencil,
  Check,
  X,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/utils/misc";
import { extractErrorMessage } from "@/utils/errors";
import { Input } from "@/ui/primitives/input";
import { Button } from "@/ui/primitives/button";

interface AdminAiUsageTableProps {
  className?: string;
}

function formatUsd(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatPeriod(yyyyMm: string): string {
  const [yyyy, mm] = yyyyMm.split("-").map((s) => parseInt(s, 10));
  if (!yyyy || !mm) return yyyyMm;
  return new Date(Date.UTC(yyyy, mm - 1, 1)).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
}

export function AdminAiUsageTable({ className }: AdminAiUsageTableProps) {
  const { data, isLoading } = useQuery(
    convexQuery(api.lib.aiUsageQueries.getPlatformUsageBreakdown, {}),
  );

  const [editingOrgId, setEditingOrgId] = useState<Id<"organizations"> | null>(
    null,
  );
  const [draftCap, setDraftCap] = useState("");
  const [saving, setSaving] = useState(false);

  const { mutateAsync: setAiUsageCap } = useMutation({
    mutationFn: useConvexMutation(api.admin.organisations.setAiUsageCap),
  });

  const startEditing = (orgId: Id<"organizations">, capUsdCents: number) => {
    setEditingOrgId(orgId);
    setDraftCap(String(Math.round(capUsdCents / 100)));
  };

  const saveCap = async (orgId: Id<"organizations">) => {
    const parsed = Number(draftCap);
    if (!Number.isFinite(parsed) || parsed < 1) {
      toast.error("Enter a monthly cap of at least $1");
      return;
    }
    setSaving(true);
    try {
      await setAiUsageCap({ orgId, capUsd: parsed });
      toast.success(`Monthly cap set to $${Math.round(parsed)}`);
      setEditingOrgId(null);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to update cap"));
    } finally {
      setSaving(false);
    }
  };

  const resetCap = async (orgId: Id<"organizations">) => {
    setSaving(true);
    try {
      await setAiUsageCap({ orgId, capUsd: null });
      toast.success("Monthly cap reset to the platform default");
      setEditingOrgId(null);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to reset cap"));
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-xl border border-border bg-card p-6",
          className,
        )}
      >
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl border border-border bg-card shadow-sm",
        className,
      )}
    >
      <div className="flex items-baseline justify-between gap-3 border-b border-border bg-background/60 px-6 py-4">
        <div className="space-y-0.5">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <h2 className="text-base font-semibold">AI usage</h2>
          </div>
          <p className="text-xs text-muted-foreground">
            Platform-wide spend for {formatPeriod(data.periodYearMonth)} ·
            Default cap is $100 per organisation per month — edit a firm&apos;s
            cap to override it.
          </p>
        </div>
        <p className="text-2xl font-semibold tracking-tight">
          {formatUsd(data.totalUsdCents)}
        </p>
      </div>

      {data.perOrg.length === 0 ? (
        <div className="px-6 py-6 text-sm text-muted-foreground">
          No AI usage recorded across any org this month.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-[10px] uppercase tracking-wider text-muted-foreground/70">
              <tr>
                <th className="px-6 py-2 text-left font-medium">
                  Organisation
                </th>
                <th className="px-4 py-2 text-right font-medium">Spend</th>
                <th className="px-4 py-2 text-right font-medium">% of cap</th>
                <th className="px-4 py-2 text-left font-medium">Status</th>
                <th className="px-4 py-2 text-right font-medium">
                  Monthly cap
                </th>
                <th className="px-4 py-2 text-right font-medium">
                  Tokens (in / out)
                </th>
              </tr>
            </thead>
            <tbody>
              {data.perOrg.map((row) => {
                const pct = Math.min(
                  100,
                  Math.round((row.usdCents / row.capUsdCents) * 100),
                );
                return (
                  <tr
                    key={row.orgId}
                    className="border-t border-border/60 hover:bg-muted/20"
                  >
                    <td className="px-6 py-2 font-medium text-foreground">
                      {row.orgName}
                    </td>
                    <td className="px-4 py-2 text-right font-medium">
                      {formatUsd(row.usdCents)}
                      <span className="text-xs text-muted-foreground">
                        {" "}
                        / {formatUsd(row.capUsdCents)}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <span
                        className={cn(
                          "inline-flex items-center justify-end font-medium",
                          row.hardBlock
                            ? "text-destructive"
                            : row.softWarn
                              ? "text-amber-600 dark:text-amber-400"
                              : "text-foreground/70",
                        )}
                      >
                        {pct}%
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <StatusPill
                        hardBlock={row.hardBlock}
                        softWarn={row.softWarn}
                      />
                    </td>
                    <td className="px-4 py-2 text-right">
                      {editingOrgId === row.orgId ? (
                        <div className="flex items-center justify-end gap-1">
                          <span className="text-xs text-muted-foreground">
                            $
                          </span>
                          <Input
                            autoFocus
                            type="number"
                            min={1}
                            step={1}
                            value={draftCap}
                            disabled={saving}
                            onChange={(e) => setDraftCap(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") void saveCap(row.orgId);
                              if (e.key === "Escape") setEditingOrgId(null);
                            }}
                            className="h-7 w-24 text-right text-sm"
                          />
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            disabled={saving}
                            onClick={() => void saveCap(row.orgId)}
                            title="Save cap"
                          >
                            {saving ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Check className="h-3.5 w-3.5" />
                            )}
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            disabled={saving}
                            onClick={() => setEditingOrgId(null)}
                            title="Cancel"
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                          {row.capOverridden && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7"
                              disabled={saving}
                              onClick={() => void resetCap(row.orgId)}
                              title="Reset to the $100 default"
                            >
                              <RotateCcw className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-1.5">
                          <span className="font-medium tabular-nums">
                            {formatUsd(row.capUsdCents)}
                          </span>
                          {row.capOverridden && (
                            <span className="rounded-full border border-primary/40 bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                              Custom
                            </span>
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-muted-foreground"
                            onClick={() =>
                              startEditing(row.orgId, row.capUsdCents)
                            }
                            title="Override this firm's monthly cap"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right text-xs tabular-nums text-muted-foreground">
                      {row.inputTokens.toLocaleString()} /{" "}
                      {row.outputTokens.toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusPill({
  hardBlock,
  softWarn,
}: {
  hardBlock: boolean;
  softWarn: boolean;
}) {
  if (hardBlock) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-destructive/40 bg-destructive/10 px-2 py-0.5 text-[10px] font-medium text-destructive">
        <OctagonAlert className="h-3 w-3" />
        Capped
      </span>
    );
  }
  if (softWarn) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-300">
        <AlertTriangle className="h-3 w-3" />
        Approaching
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-300">
      <CheckCircle2 className="h-3 w-3" />
      Within budget
    </span>
  );
}
